const TOKEN_KEY = 'ibma_assessment_token';
const USER_KEY = 'ibma_assessment_user';
const ADMIN_TOKEN_KEY = 'ibma_admin_token';
const ADMIN_USER_KEY = 'ibma_admin_user';

// Shared cache for admin data (avoids redundant SharePoint calls when switching tabs)
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_KEYS = {
  students: 'cache_students',
  submissions: 'cache_submissions',
};

// Event-based cache invalidation — components listen for this to re-fetch data
export const ADMIN_CACHE_INVALIDATED_EVENT = 'admin-cache-invalidated';

function getFromCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
}

export function saveSession(token, user) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getUser() {
  const raw = sessionStorage.getItem(USER_KEY);
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function saveAdminSession(token, user) {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  sessionStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
}

export function getAdminUser() {
  const raw = sessionStorage.getItem(ADMIN_USER_KEY);
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function clearSession() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

// Alias for clarity — same as clearSession
export const clearStudentSession = clearSession;

export function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_USER_KEY);
}

// Whitelist of endpoints that require admin authentication
const ADMIN_ENDPOINTS = new Set([
  'admin-login',
  'change-student-password',
  'debug-attempts',
  'get-submissions',
  'get-students',
  'import-students-xlsx',
  'get-student-detail',
  'reset-unit-attempts',
  'get-unit-attempts',
  'undo-import-students',
  'get-unit-stats',
  'update-student-units',
  'list-question-banks',
  'download-import-errors',
]);

async function request(path, { method = 'GET', body, signal } = {}) {
  const headers = { 'Content-Type': 'application/json' };

  // Admin endpoints use separate session storage
  let token;
  const endpointName = path.split('?')[0]; // strip query params for matching
  if (ADMIN_ENDPOINTS.has(endpointName)) {
    token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || getToken();
  } else {
    token = getToken();
  }

  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`/.netlify/functions/${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  login: (username, password) => request('login', { method: 'POST', body: { username, password } }),
  adminLogin: (username, password) => request('admin-login', { method: 'POST', body: { username, password } }),
  getSubmissions: () => getCached(CACHE_KEYS.submissions, () => request('get-submissions')),
  getStudents: () => getCached(CACHE_KEYS.students, () => request('get-students')),
  importStudentsFromCSV: (filename, csvContent) => {
    const result = request('import-students-xlsx', { 
      method: 'POST', 
      body: { filename, csvContent }
    });
    clearAdminCache();
    return result;
  },
 importStudentsFromXLSX: (filename, base64Content) => {
    const result = request('import-students-xlsx', { 
      method: 'POST', 
      body: { filename, base64Content }
    });
    clearAdminCache();
    return result;
  },
  undoImportStudents: (undoToken) =>
    request('undo-import-students', { 
      method: 'POST', 
      body: { undoToken }
    }).then(result => {
      clearAdminCache();
      return result;
    }),
  getQuestions: (unitCode, course) => {
    const params = new URLSearchParams();
    if (unitCode) params.set('unitCode', unitCode);
    if (course) params.set('course', course);
    const qs = params.toString();
    return request(`get-questions${qs ? `?${qs}` : ''}`);
  },
  submit: (answers, unitCode, sessionId, course) => {
    const params = new URLSearchParams();
    if (unitCode) params.set('unitCode', unitCode);
    if (course) params.set('course', course);
    const qs = params.toString();
    return request(`submit${qs ? `?${qs}` : ''}`, { method: 'POST', body: { answers, sessionId } }).then(result => {
      clearUnitStatsCache();
      return result;
    });
  },
  revealAnswers: (username, password, questionIds, unitCode, sessionId, course) => {
    const params = new URLSearchParams();
    if (unitCode) params.set('unitCode', unitCode);
    if (course) params.set('course', course);
    const qs = params.toString();
    return request(`reveal-answers${qs ? `?${qs}` : ''}`, { method: 'POST', body: { username, password, questionIds, sessionId } });
  },
  getUnitStats: (unitCode, course, optionalID, studentUsername, signal) => {
    const params = new URLSearchParams({ unitCode, course });
    if (optionalID) params.set('optionalID', optionalID);
    if (studentUsername) params.set('studentUsername', studentUsername);
    // Include all identifiers in cache key so each student's stats are cached separately
    const cacheKey = `cache_unit_stats_${unitCode}_${course}_${(optionalID || '').trim()}_${(studentUsername || '').trim()}`;
    return getCached(cacheKey, () => request(`get-unit-stats?${params.toString()}`, { signal }));
  },
  resetUnitAttempts: ({ username, optionalID, course, unitCode }) =>
    request('reset-unit-attempts', { method: 'POST', body: { username, optionalID, course, unitCode } }).then(result => {
      clearAdminCache();
      return result;
    }),
  getStudentDetail: (optionalID) => {
    const params = new URLSearchParams({ optionalID });
    return request(`get-student-detail?${params.toString()}&password=true`);
  },
  changeStudentPassword: ({ optionalID, newPassword }) =>
    request('change-student-password', { method: 'POST', body: { optionalID, newPassword } }).then(result => {
      clearAdminCache();
      return result;
    }),
  // Admin-only version of getUnitStats (delegates to cached request with admin token)
  getUnitStatsAdmin: (unitCode, course, studentUsername, optionalID) => {
    return api.getUnitStats(unitCode, course, studentUsername, optionalID);
  },
  // Get full attempt data (with answers) for a specific student+unit (admin token)
  getUnitAttempts: async (course, unitCode, optionalID) => {
    const headers = { 'Content-Type': 'application/json' };
    const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const params = new URLSearchParams({ course, unitCode });
    if (optionalID) params.set('optionalID', optionalID);
    const res = await fetch(`/.netlify/functions/get-unit-attempts?${params.toString()}`, {
      method: 'GET',
      headers,
    });
    const data = await res.json();
    if (!res.ok || data.error) return { attempts: [], mappingPaths: [] };
    return { attempts: data.attempts || [], mappingPaths: data.mappingPaths || [] };
  },
  updateStudentUnits: ({ optionalID, qualificationName, add, remove, update }) =>
    request('update-student-units', { method: 'POST', body: { optionalID, qualificationName, add, remove, update } }).then(result => {
      clearAdminCache();
      return result;
    }),
  updateUnitDates: ({ optionalID, qualificationName, code, activityStartDate, activityEndDate }) =>
    request('update-student-units', { method: 'POST', body: { optionalID, qualificationName, update: [{ code, activityStartDate, activityEndDate }] } }).then(result => {
      clearAdminCache();
      return result;
    }),
  listQuestionBanks: () => request('list-question-banks'),
  downloadImportErrors: (file, format) => {
    const params = new URLSearchParams({ file, format });
    return request(`download-import-errors?${params.toString()}`);
  },
  saveBase64File: ({ filename, mimeType, base64 }) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

function getCached(key, fetchFn) {
  const cached = getFromCache(key);
  if (cached) return Promise.resolve(cached);
  return fetchFn().then(data => { setCache(key, data); return data; });
}

function clearAdminCache() {
  sessionStorage.removeItem(CACHE_KEYS.students);
  sessionStorage.removeItem(CACHE_KEYS.submissions);
  window.dispatchEvent(new CustomEvent(ADMIN_CACHE_INVALIDATED_EVENT));
}

function clearUnitStatsCache() {
  const prefix = 'cache_unit_stats_';
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(prefix)) {
      sessionStorage.removeItem(key);
    }
  }
}

export function invalidateAdminCache() {
  clearAdminCache();
}
